import React, { memo, useMemo, useCallback, ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Theme,
  Typography,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { trackEvent } from '@surfline/web-common';
import { Information, InformationProps } from 'components/Information/Information';
import classNames from 'classnames';
import styles from './ForecastGraphAccordion.module.scss';

const accordionSummaryStyles = (isOpen: boolean) => (t: Theme) => ({
  borderBottom: 'solid 1px',
  borderColor: isOpen ? 'transparent' : t.palette.divider,
  flexDirection: 'row-reverse',
  paddingLeft: 0,
  paddingRight: 0,
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: 0,
    marginRight: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {},
  '& .MuiAccordionSummary-content': {
    marginLeft: '10px',
  },
});

const accordionStyles = {
  '&:before': {
    display: 'none',
  },
};

const customAccordionTransitionProps = { unmountOnExit: true };

interface Props {
  className?: string;
  children: ReactNode;
  customTrackingValues?: { title?: string; properties?: Record<string, unknown> };
  id: string;
  information?: InformationProps;
  labs?: InformationProps;
  isOpen: boolean;
  label: ReactNode;
  disableFullBleed?: boolean;
  message?: ReactNode;
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

const ForecastGraphAccordion: React.FC<Props> = ({
  className,
  children,
  disableFullBleed,
  id,
  information,
  isOpen,
  label,
  labs,
  message,
  onChange,
  customTrackingValues,
}) => {
  const contentId = useMemo(() => `${id}-content`, [id]);
  const disableFullBleedSummary = useMemo(
    () => ({
      padding: 0,
    }),
    [],
  );
  const handleChange = useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      trackEvent(customTrackingValues?.title || 'Clicked Module', {
        moduleName: id,
        pageName: 'Spot Report',
        uiElement: isExpanded ? 'expand arrow' : 'collapse arrow',
        ...customTrackingValues?.properties,
      });

      if (onChange) onChange(event, isExpanded);
    },
    [customTrackingValues, id, onChange],
  );

  const AccordionContents = useMemo(
    () => (
      <>
        <Typography
          variant="callout1"
          component="p"
          className={styles.label}
          data-testid="forecast-graph-accordion-label"
        >
          {label}
        </Typography>
        {message || null}
      </>
    ),
    [label, message],
  );

  return (
    <Box className={classNames(styles.container, className)}>
      <Accordion
        className={classNames(!disableFullBleed ? 'sl-section-full-bleed' : '', styles.accordion)}
        disableGutters
        elevation={0}
        expanded={isOpen}
        onChange={handleChange}
        square
        sx={accordionStyles}
        data-testid="forecast-graph-accordion"
        slotProps={{
          transition: customAccordionTransitionProps,
        }}
      >
        <AccordionSummary
          aria-controls={contentId}
          expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
          sx={accordionSummaryStyles(isOpen)}
          data-testid="forecast-graph-accordion-summary"
          style={disableFullBleed ? disableFullBleedSummary : {}}
        >
          {AccordionContents}
        </AccordionSummary>
        <AccordionDetails className={styles.details}>{children}</AccordionDetails>
      </Accordion>
      <Box className={styles.infoContainer}>
        {labs && (
          <Information
            className={styles.labs}
            tooltip="Surfline Labs features are experimental"
            {...labs}
            data-testid="forecast-graph-accordion-labs"
          >
            <Chip label="Labs" color="secondary" className={styles.chip} />
          </Information>
        )}
        {information && (
          <Information
            className={styles.info}
            {...information}
            data-testid="forecast-graph-accordion-info"
          />
        )}
      </Box>
    </Box>
  );
};

export default memo(ForecastGraphAccordion);
